import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getBattleAgentRunHistory, getBattleAgents } from '../Api';
import type {
    BattleAgentDto,
    BattleAgentRunHistoryItemDto,
    BattleAgentRunHistoryPageDto,
} from '../models/dtos/BattleAgentDto';

const PAGE_SIZE = 25;

export default function BattleAgentsHistoryPage() {
    const navigate = useNavigate();
    const [agents, setAgents] = React.useState<BattleAgentDto[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [page, setPage] = React.useState<BattleAgentRunHistoryPageDto | null>(null);
    const [offset, setOffset] = React.useState(0);

    const [agentId, setAgentId] = React.useState('');
    const [creatorUserId, setCreatorUserId] = React.useState('');
    const [inviteeUserId, setInviteeUserId] = React.useState('');
    const [status, setStatus] = React.useState('');
    const [mode, setMode] = React.useState('');
    const [onlyWithChallenge, setOnlyWithChallenge] = React.useState(false);

    const load = React.useCallback(async (nextOffset = 0, opts?: { keepPrevious?: boolean }) => {
        if (!opts?.keepPrevious) {
            setLoading(true);
        }
        setError(null);
        const result = await getBattleAgentRunHistory({
            agentId: agentId || undefined,
            creatorUserId: creatorUserId || undefined,
            inviteeUserId: inviteeUserId || undefined,
            status: status || undefined,
            mode: mode || undefined,
            onlyWithChallenge: onlyWithChallenge || undefined,
            offset: nextOffset,
            limit: PAGE_SIZE,
        });
        if (!result) {
            setError('Failed to load battle agent history.');
            if (!opts?.keepPrevious) {
                setPage(null);
            }
        } else {
            setPage(result);
            setOffset(nextOffset);
        }
        setLoading(false);
    }, [agentId, creatorUserId, inviteeUserId, status, mode, onlyWithChallenge]);

    React.useEffect(() => {
        (async () => {
            const list = await getBattleAgents();
            setAgents(list ?? []);
            await load(0);
        })();
        // initial load only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => load(0);

    const totalPages = page ? Math.max(1, Math.ceil(page.totalItems / PAGE_SIZE)) : 1;
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    const acceptanceChip = (row: BattleAgentRunHistoryItemDto) => {
        if (row.opponentAccepted === true) {
            return <Chip size="small" color="success" label={row.acceptanceLabel ?? 'Accepted'} />;
        }
        if (row.opponentAccepted === false) {
            return <Chip size="small" color="warning" label={row.acceptanceLabel ?? 'Not accepted'} />;
        }
        return <Chip size="small" label={row.acceptanceLabel ?? '—'} />;
    };

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Typography variant="h4">Battle agents — history</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={() => navigate('/battle-agents')}>Back to agents</Button>
                        <Button onClick={() => navigate('/battle-agents/stats')}>Stats</Button>
                        <Button variant="outlined" onClick={applyFilters} disabled={loading}>Refresh</Button>
                    </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    All agent runs across create + auto-accept. Filter by agent, bot creator, invitee, status/mode.
                    Open a battle to see full details.
                </Typography>

                {page?.summary && (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`Success 24h: ${page.summary.successLast24h}`} color="success" />
                        <Chip label={`Skipped 24h: ${page.summary.skippedLast24h}`} />
                        <Chip label={`Failed 24h: ${page.summary.failedLast24h}`} color="error" />
                        <Chip label={`Battles in view: ${page.summary.createdBattlesInResult}`} />
                        <Chip label={`Pending: ${page.summary.pendingInResult}`} color="warning" />
                        <Chip label={`Accepted/Started: ${page.summary.acceptedOrStartedInResult}`} color="success" />
                        <Chip label={`Finished: ${page.summary.finishedInResult}`} />
                    </Stack>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Agent</InputLabel>
                        <Select label="Agent" value={agentId} onChange={e => setAgentId(e.target.value)}>
                            <MenuItem value="">All agents</MenuItem>
                            {agents.map(a => (
                                <MenuItem key={a.id} value={a.id}>{a.name} ({a.slug})</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField label="Creator user ID" value={creatorUserId} onChange={e => setCreatorUserId(e.target.value)} sx={{ minWidth: 160 }} />
                    <TextField label="Invitee user ID" value={inviteeUserId} onChange={e => setInviteeUserId(e.target.value)} sx={{ minWidth: 160 }} />
                    <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={status} onChange={e => setStatus(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                            <MenuItem value="SKIPPED">SKIPPED</MenuItem>
                            <MenuItem value="FAILED">FAILED</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 140 }}>
                        <InputLabel>Mode</InputLabel>
                        <Select label="Mode" value={mode} onChange={e => setMode(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="PUBLIC">PUBLIC</MenuItem>
                            <MenuItem value="PRIVATE">PRIVATE</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={<Switch checked={onlyWithChallenge} onChange={e => setOnlyWithChallenge(e.target.checked)} />}
                        label="Only with battle"
                    />
                    <Button variant="contained" onClick={applyFilters} disabled={loading}>Apply filters</Button>
                </Stack>

                {error && <Alert severity="error">{error}</Alert>}
                {loading && !page && <CircularProgress />}
                {loading && page && (
                    <Typography variant="body2" color="text.secondary">Refreshing…</Typography>
                )}

                {page && (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            Showing {page.items.length} of {page.totalItems} runs
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Mode</TableCell>
                                    <TableCell>Creator bot</TableCell>
                                    <TableCell>Invitee / opponent</TableCell>
                                    <TableCell>Stake</TableCell>
                                    <TableCell>Acceptance</TableCell>
                                    <TableCell>Participants</TableCell>
                                    <TableCell>Battle</TableCell>
                                    <TableCell>Message</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {page.items.map(row => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {new Date(row.createdAt).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <Button size="small" onClick={() => navigate('/battle-agents/' + row.agentId + '/edit')}>
                                                {row.agentSlug}
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.status}
                                                color={
                                                    row.status === 'SUCCESS' ? 'success'
                                                        : row.status === 'FAILED' ? 'error'
                                                            : 'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>{row.mode ?? '—'}</TableCell>
                                        <TableCell>
                                            {row.creatorUserId != null ? (
                                                <Button size="small" onClick={() => navigate('/users/' + row.creatorUserId)}>
                                                    #{row.creatorUserId}
                                                </Button>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {row.inviteeUserId != null ? (
                                                <Button size="small" onClick={() => navigate('/users/' + row.inviteeUserId)}>
                                                    #{row.inviteeUserId}
                                                </Button>
                                            ) : (
                                                row.participants.find(p => !p.isCreator)?.userId != null
                                                    ? (
                                                        <Button
                                                            size="small"
                                                            onClick={() => navigate('/users/' + row.participants.find(p => !p.isCreator)!.userId)}
                                                        >
                                                            #{row.participants.find(p => !p.isCreator)!.userId}
                                                        </Button>
                                                    )
                                                    : '—'
                                            )}
                                        </TableCell>
                                        <TableCell>{row.moneyPlaced ?? '—'}</TableCell>
                                        <TableCell>{acceptanceChip(row)}</TableCell>
                                        <TableCell>
                                            {row.participants.length === 0
                                                ? '—'
                                                : row.participants.map(p => (
                                                    `${p.isCreator ? 'C' : 'O'}:${p.userName}(${p.stateLabel})`
                                                )).join(' · ')}
                                        </TableCell>
                                        <TableCell>
                                            {row.challengeId != null ? (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => navigate('/battles/' + row.challengeId)}
                                                >
                                                    #{row.challengeId}
                                                    {row.challengeStateLabel ? ` · ${row.challengeStateLabel}` : ''}
                                                </Button>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell>{row.message ?? '—'}</TableCell>
                                    </TableRow>
                                ))}
                                {page.items.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={11}>
                                            <Typography color="text.secondary">No runs match these filters.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, p) => load((p - 1) * PAGE_SIZE, { keepPrevious: true })}
                        />
                    </>
                )}
            </Stack>
        </Box>
    );
}
